import type { Span } from './span';

/**
 * Screen-pixel reach of the neighbour detent (redesign §2.1.2): a dragged edge within this many
 * pixels of a boundary stays on it, on either side, so the pointer must travel this far past the
 * boundary to leave it.
 */
export const DETENT_THRESHOLD_PX = 8;

/**
 * The boundaries a created or resized edge can stick to: both edges of every other block plus
 * the personal-day bounds, sorted and deduplicated.
 */
export function detentBoundaries(others: readonly Span[], bounds: Span): number[] {
  const unique = new Set<number>([bounds.startMin, bounds.endMin]);
  for (const other of others) {
    unique.add(other.startMin);
    unique.add(other.endMin);
  }
  return [...unique].sort((a, b) => a - b);
}

/** Outcome of the neighbour detent: the minute to use and whether a boundary was hit. */
export interface DetentResult {
  readonly min: number;
  /** A boundary held the edge: the caller uses the exact minute and skips step snapping (§3.6). */
  readonly hit: boolean;
}

/**
 * Neighbour detent for creating and resizing — never for whole-block moving (redesign §2.1.2):
 * when the raw pointer minute lies within `thresholdPx` screen pixels of a boundary, the result
 * is that boundary, marked `hit` so the caller keeps the neighbour's exact minute and skips the
 * step snapping (2026-10b §3.6) — a boundary at 14:10 stays 14:10 instead of leaving a gap or
 * an overlap. Elsewhere the raw minute is returned unchanged, to be snapped by the caller.
 * `pxPerMin` converts the pixel threshold into minutes; the caller bounds the threshold by one
 * step's pixel height (`min(8px, step height)`, §3.6), so a fine step keeps nearby positions
 * reachable. The nearest boundary wins; exact ties go to the earlier one. The hold is
 * stateless, with no hysteresis: an edge entering the reach from either side sits on the
 * boundary, up to `thresholdPx` before the pointer actually arrives there (accepted).
 */
export function applyDetent(
  rawMin: number,
  boundaries: readonly number[],
  pxPerMin: number,
  thresholdPx: number = DETENT_THRESHOLD_PX,
): DetentResult {
  if (pxPerMin <= 0) return { min: rawMin, hit: false };
  const reachMin = thresholdPx / pxPerMin;
  let best: number | null = null;
  for (const boundary of boundaries) {
    const distance = Math.abs(rawMin - boundary);
    if (distance > reachMin) continue;
    if (best === null || distance < Math.abs(rawMin - best)) best = boundary;
  }
  return best === null ? { min: rawMin, hit: false } : { min: best, hit: true };
}
